import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdVideocam, MdMic, MdRefresh, MdCircle } from 'react-icons/md';
import { FiRefreshCw } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { useRoleMessages } from '../../contexts/RoleMessagesContext';
import InterpreterInvitationModal from '../VideoCall/InterpreterInvitationModal';
import './InterpreterDashboard.css';
import './Dashboard.css';

const InterpreterDashboard = () => {
  const navigate = useNavigate();
  const socket = useSocket();
  const { getMessage } = useRoleMessages();

  const [stream, setStream] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [interpreterStatus, setInterpreterStatus] = useState('online');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const videoRef = useRef(null);

  // Get role message from context. The interpreter dashboard lives at /dashboard,
  // so admins configure it under the "dashboard" screen. Fall back to the legacy
  // "interpreter-dashboard" screen for any previously created instructions.
  const infoBannerMessage =
    getMessage('dashboard', 'welcome') || getMessage('interpreter-dashboard', 'welcome') || '';

  // Status options with colors
  const statusOptions = [
    { value: 'online', label: 'Online', color: '#48bb78', description: 'Available for calls' },
    { value: 'away', label: 'Away', color: '#ed8936', description: 'Temporarily unavailable' },
    { value: 'busy', label: 'Busy', color: '#f56565', description: 'Do not disturb' }
  ];

  // Get current status
  const fetchCurrentStatus = useCallback(() => {
    if (socket?.socket) {
      socket.socket.emit('get-interpreter-status');
    }
  }, [socket]);

  // Set interpreter status
  const setStatus = useCallback((newStatus) => {
    if (!socket?.socket) return;
    
    setStatusUpdating(true);
    socket.socket.emit('set-interpreter-status', { status: newStatus });
  }, [socket]);

  // Listen for status updates
  useEffect(() => {
    if (!socket?.socket) return;

    const handleStatusUpdated = (data) => {
      console.log('✅ Status updated:', data);
      setInterpreterStatus(data.status);
      setStatusUpdating(false);
    };

    const handleCurrentStatus = (data) => {
      console.log('📊 Current status:', data);
      setInterpreterStatus(data.status);
    };

    socket.socket.on('interpreter-status-updated', handleStatusUpdated);
    socket.socket.on('interpreter-status', handleCurrentStatus);

    // Fetch current status on mount
    fetchCurrentStatus();

    return () => {
      socket.socket.off('interpreter-status-updated', handleStatusUpdated);
      socket.socket.off('interpreter-status', handleCurrentStatus);
    };
  }, [socket, fetchCurrentStatus]);

  // Socket listener for interpreter invitations
  useEffect(() => {
    if (!socket?.socket) {
      console.log('⚠️ Socket not available for interpreter invitations');
      return;
    }

    console.log('✅ Listening for interpreter invitations on socket');

    const handleInvitation = (data) => {
      console.log('📞 Interpreter invitation received:', data);
      setInvitation(data);
      setShowInvitationModal(true);

      // Play notification sound (ting)
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      } catch (e) {
        console.log('Could not play sound:', e);
      }

      // Text-to-speech announcement
      try {
        const announcement = `You have been invited to join a video call`;
        const utterance = new SpeechSynthesisUtterance(announcement);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        window.speechSynthesis.speak(utterance);
      } catch (e) {
        console.log('Could not play announcement:', e);
      }
    };

    socket.socket.on('interpreter_invitation', handleInvitation);

    return () => {
      socket.socket.off('interpreter_invitation', handleInvitation);
    };
  }, [socket]);

  useEffect(() => {
    // Cleanup stream when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Load available devices on mount
  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Request permissions first to get device labels
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        tempStream.getTracks().forEach(track => track.stop());

        // Now enumerate devices
        const devices = await navigator.mediaDevices.enumerateDevices();

        const audioInputs = devices.filter(device => device.kind === 'audioinput');
        const videoInputs = devices.filter(device => device.kind === 'videoinput');

        setAudioDevices(audioInputs);
        setVideoDevices(videoInputs);

        // Set default selections
        if (audioInputs.length > 0 && !selectedAudioDevice) {
          setSelectedAudioDevice(audioInputs[0].deviceId);
        }
        if (videoInputs.length > 0 && !selectedVideoDevice) {
          setSelectedVideoDevice(videoInputs[0].deviceId);
        }
      } catch (err) {
        console.error('Error loading devices:', err);
        setError('Could not load devices. Please grant camera/microphone permissions.');
      }
    };

    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const startCameraTest = async () => {
    try {
      setError('');
      setTesting(true);

      // Build constraints with selected devices
      // Use wider aspect ratio (16:9) for better hand visibility in sign language interpretation
      const constraints = {
        video: selectedVideoDevice ? {
          deviceId: { exact: selectedVideoDevice },
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          aspectRatio: { ideal: 16 / 9 }
        } : {
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          aspectRatio: { ideal: 16 / 9 }
        },
        audio: selectedAudioDevice ? {
          deviceId: { exact: selectedAudioDevice }
        } : true
      };

      // Request both audio and video permissions
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      setStream(mediaStream);

      // Attach stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      // Check which tracks are enabled
      const videoTrack = mediaStream.getVideoTracks()[0];
      const audioTrack = mediaStream.getAudioTracks()[0];

      if (videoTrack) {
        setVideoEnabled(true);
      }
      if (audioTrack) {
        setAudioEnabled(true);
      }

    } catch (err) {
      console.error('Camera test error:', err);
      setError(`Failed to access camera/microphone: ${err.message}`);
      setTesting(false);
    }
  };

  const stopCameraTest = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setVideoEnabled(false);
      setAudioEnabled(false);
      setTesting(false);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const toggleAudio = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  const handleAudioDeviceChange = async (deviceId) => {
    setSelectedAudioDevice(deviceId);

    // If testing, restart with new device
    if (testing && stream) {
      const wasAudioEnabled = audioEnabled;
      stopCameraTest();
      // Small delay to ensure cleanup and state update
      setTimeout(async () => {
        await startCameraTest();
        if (!wasAudioEnabled && stream) {
          toggleAudio();
        }
      }, 100);
    }
  };

  const handleVideoDeviceChange = async (deviceId) => {
    setSelectedVideoDevice(deviceId);

    // If testing, restart with new device
    if (testing && stream) {
      const wasVideoEnabled = videoEnabled;
      stopCameraTest();
      // Small delay to ensure cleanup and state update
      setTimeout(async () => {
        await startCameraTest();
        if (!wasVideoEnabled && stream) {
          toggleVideo();
        }
      }, 100);
    }
  };

  // Handle invitation acceptance
  const handleAcceptInvitation = (invitationData) => {
    console.log('Accepting invitation:', invitationData);

    // Send response to server
    if (socket?.socket) {
      socket.socket.emit('interpreter-response', {
        callId: invitationData.callId,
        response: 'accept',
        devicePreferences: {
          audioDeviceId: invitationData.selectedAudioDevice,
          videoDeviceId: invitationData.selectedVideoDevice,
          audioEnabled: invitationData.audioEnabled,
          videoEnabled: invitationData.videoEnabled
        }
      });

      // Listen for confirmation
      socket.socket.once('interpreter-accepted-confirmation', (data) => {
        console.log('Confirmation received:', data);
        // Navigate to video call with call data
        navigate(`/video-call/${data.callId}`, {
          state: {
            callData: {
              callId: data.callId,
              roomName: data.roomName,
              accessToken: data.accessToken,
              userRole: 'interpreter',
              booth: data.booth,
              event: data.event,
              participants: {
                recruiter: data.recruiter,
                jobSeeker: data.jobSeeker
              },
              metadata: data.metadata
            }
          }
        });
      });
    }

    // Close modal
    setShowInvitationModal(false);
    setInvitation(null);
  };

  // Handle invitation rejection
  const handleRejectInvitation = (invitationData) => {
    console.log('Rejecting invitation:', invitationData);

    // Send response to server
    if (socket?.socket) {
      socket.socket.emit('interpreter-response', {
        callId: invitationData.callId,
        response: 'decline'
      });
    }

    // Close modal
    setShowInvitationModal(false);
    setInvitation(null);
  };

  return (
    <div className="interpreter-dashboard">
      <div className="interpreter-content">
        {infoBannerMessage && (
          <div className="info-banner" style={{ marginBottom: '1.5rem' }}>
            <span>{infoBannerMessage}</span>
          </div>
        )}
        <div className="waiting-section">
          <h1>Interpreter Waiting Section</h1>
          <p className="waiting-message">
            You have successfully joined the queue. Wait for the interviewer to let you into the call.
          </p>
        </div>

        <div className="camera-test-section">
          <h2>Camera & Microphone Test</h2>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <div className="instructions">
            <h3>Instructions:</h3>
            <ul>
              <li>Click "Start Test" to check your camera and microphone</li>
              <li>Make sure your camera shows a clear picture</li>
              <li>Speak to test your microphone (check the browser's permission prompt)</li>
              <li>You can toggle audio/video during the test</li>
              <li>Once you're ready, wait for the interviewer to invite you</li>
            </ul>
          </div>

          <div className="camera-test-layout">
            <div className="camera-test-left">
              <div className="device-selectors">
                {/* Status Selector - Compact Dropdown */}
                <div className="device-selector status-selector-compact">
                  <label htmlFor="status-select">
                    <MdCircle 
                      size={12} 
                      style={{ color: statusOptions.find(s => s.value === interpreterStatus)?.color || '#48bb78' }} 
                    />
                    Status:
                  </label>
                  <div className="status-select-wrapper">
                    <select
                      id="status-select"
                      value={interpreterStatus}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={statusUpdating}
                      className="device-select status-select"
                      style={{
                        borderColor: statusOptions.find(s => s.value === interpreterStatus)?.color || '#48bb78'
                      }}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} - {option.description}
                        </option>
                      ))}
                    </select>
                    <button 
                      className="status-refresh-btn"
                      onClick={fetchCurrentStatus}
                      disabled={statusUpdating}
                      title="Refresh status"
                    >
                      <FiRefreshCw size={14} className={statusUpdating ? 'spinning' : ''} />
                    </button>
                  </div>
                </div>

                <div className="device-selector">
                  <label htmlFor="video-device-select">
                    <MdVideocam size={18} />
                    Camera:
                  </label>
                  <select
                    id="video-device-select"
                    value={selectedVideoDevice}
                    onChange={(e) => handleVideoDeviceChange(e.target.value)}
                    className="device-select"
                  >
                    {videoDevices.length === 0 && (
                      <option value="">No cameras found</option>
                    )}
                    {videoDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${videoDevices.indexOf(device) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="device-selector">
                  <label htmlFor="audio-device-select">
                    <MdMic size={18} />
                    Microphone:
                  </label>
                  <select
                    id="audio-device-select"
                    value={selectedAudioDevice}
                    onChange={(e) => handleAudioDeviceChange(e.target.value)}
                    className="device-select"
                  >
                    {audioDevices.length === 0 && (
                      <option value="">No microphones found</option>
                    )}
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${audioDevices.indexOf(device) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="test-controls">
                {!testing ? (
                  <button
                    className="test-button primary"
                    onClick={startCameraTest}
                  >
                    <MdRefresh size={20} />
                    Start Test
                  </button>
                ) : (
                  <>
                    <button
                      className={`test-button ${audioEnabled ? 'active' : 'inactive'}`}
                      onClick={toggleAudio}
                      title={audioEnabled ? 'Mute' : 'Unmute'}
                    >
                      <MdMic size={20} />
                      {audioEnabled ? 'Mute' : 'Unmute'}
                    </button>

                    <button
                      className={`test-button ${videoEnabled ? 'active' : 'inactive'}`}
                      onClick={toggleVideo}
                      title={videoEnabled ? 'Stop Video' : 'Start Video'}
                    >
                      <MdVideocam size={20} />
                      {videoEnabled ? 'Stop Video' : 'Start Video'}
                    </button>

                    <button
                      className="test-button danger"
                      onClick={stopCameraTest}
                    >
                      Stop Test
                    </button>
                  </>
                )}
              </div>

              <div className="test-status">
                <div className={`status-item ${videoEnabled ? 'enabled' : 'disabled'}`}>
                  <MdVideocam size={18} />
                  <span>Camera: {videoEnabled ? 'Working' : 'Not Active'}</span>
                </div>
                <div className={`status-item ${audioEnabled ? 'enabled' : 'disabled'}`}>
                  <MdMic size={18} />
                  <span>Microphone: {audioEnabled ? 'Working' : 'Not Active'}</span>
                </div>
              </div>
            </div>

            <div className="camera-test-right">
              <div className="video-preview-container">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`video-preview ${!testing || !videoEnabled ? 'disabled' : ''}`}
                />
                {!testing && (
                  <div className="video-placeholder">
                    <MdVideocam size={64} />
                    <p>Click "Start Test" to preview your camera</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interpreter Invitation Modal */}
      {showInvitationModal && invitation && (
        <InterpreterInvitationModal
          invitation={invitation}
          onAccept={handleAcceptInvitation}
          onReject={handleRejectInvitation}
        />
      )}
    </div>
  );
};

export default InterpreterDashboard;
